import React, { useState } from "react";
import jsPDF from "jspdf";

const EmbCalci = () => {
  const [blankPrice, setBlankPrice] = useState("");
  const [totalQty, setTotalQty] = useState("");
  const [numLocations, setNumLocations] = useState(1);
  const [stitchCounts, setStitchCounts] = useState([""]);
  const [setupFee, setSetupFee] = useState("");
  const [profitMargin, setProfitMargin] = useState(0.25);
  const [customMargin, setCustomMargin] = useState("");
  const [result, setResult] = useState(null);

  const pricingMatrix = [
    { min: 1, max: 2499, prices: [9.75, 4.39, 3.51, 2.98, 2.63, 2.45, 2.28] },
    { min: 2500, max: 4999, prices: [9.75, 4.91, 4.03, 3.15, 2.86, 2.64, 2.48] },
    { min: 5000, max: 7499, prices: [9.75, 5.90, 5.00, 4.38, 4.15, 3.99, 3.81] },
    { min: 7500, max: 9999, prices: [13.33, 7.00, 6.04, 5.90, 5.00, 4.94, 4.78] },
    { min: 10000, max: 12499, prices: [14.63, 7.96, 7.00, 6.45, 5.81, 5.65, 5.49] },
    { min: 12500, max: 14999, prices: [15.84, 9.09, 7.64, 7.16, 6.14, 5.90, 5.81] },
    { min: 15000, max: 17499, prices: [16.25, 10.26, 8.38, 8.13, 6.84, 6.70, 6.61] },
    { min: 17500, max: 19999, prices: [18.69, 10.68, 9.09, 8.91, 7.49, 7.33, 7.25] },
    { min: 20000, max: 22499, prices: [20.31, 11.70, 10.51, 9.55, 8.60, 8.51, 8.36] },
    { min: 22500, max: 24999, prices: [21.91, 12.66, 12.41, 10.35, 8.84, 7.43, 8.51] }
  ];

  const qtyBrackets = [
    { min: 1, max: 11 },
    { min: 12, max: 35 },
    { min: 36, max: 95 },
    { min: 96, max: 143 },
    { min: 144, max: 499 },
    { min: 500, max: 999 },
    { min: 1000, max: Infinity }
  ];

  const findPrice = (stitch, qty) => {
    const stitchRow = pricingMatrix.find(
      (row) => stitch >= row.min && stitch <= row.max
    );
    if (!stitchRow) return 0;
    const qtyCol = qtyBrackets.findIndex(
      (range) => qty >= range.min && qty <= range.max
    );
    return stitchRow.prices[qtyCol] || 0;
  };

  const handleCalculate = () => {
    if (!blankPrice || !totalQty || !setupFee || stitchCounts.some((s) => !s)) {
      alert("Please fill out all fields!");
      return;
    }

    const qty = parseFloat(totalQty) || 0;
    const blank = parseFloat(blankPrice) || 0;
    const setup = parseFloat(setupFee) || 0;

    let embroideryCost = 0;
    stitchCounts.forEach((stitch) => {
      embroideryCost += findPrice(parseFloat(stitch) || 0, qty) * qty;
    });

    const garmentCost = blank * qty;
    const baseCost = garmentCost + embroideryCost + setup;

    const margin = customMargin !== "" ? parseFloat(customMargin) / 100 : profitMargin;

    const total = baseCost * (1 + margin);
    const perItemCost = qty > 0 ? (total / qty).toFixed(2) : 0;

    setResult({
      embroideryCost,
      garmentCost,
      setupFee: setup,
      total,
      perItemCost
    });
  };

  const handleLocationChange = (e) => {
    const count = parseInt(e.target.value) || 1;
    setNumLocations(count);
    setStitchCounts(Array(count).fill(""));
  };

  const handleStitchChange = (index, value) => {
    const updated = [...stitchCounts];
    updated[index] = value;
    setStitchCounts(updated);
  };

  const handleDownloadPDF = () => {
    const doc = new jsPDF();
    doc.setFontSize(16);
    doc.text("Embroidery Cost Estimate", 20, 20);

    doc.setFontSize(12);
    doc.text(`Blank Garment Price: $${blankPrice}`, 20, 40);
    doc.text(`Total Quantity: ${totalQty}`, 20, 50);
    doc.text(`Number of Locations: ${numLocations}`, 20, 60);

    stitchCounts.forEach((stitch, idx) => {
      doc.text(`Stitch Count (Location ${idx + 1}): ${stitch}`, 20, 70 + idx * 10);
    });

    const afterStitchLine = 70 + stitchCounts.length * 10;
    doc.text(`Setup Fee: $${setupFee}`, 20, afterStitchLine + 10);
    doc.text(`Embroidery Cost: $${result.embroideryCost.toFixed(2)}`, 20, afterStitchLine + 20);
    doc.text(`Garment Cost: $${result.garmentCost.toFixed(2)}`, 20, afterStitchLine + 30);
    doc.text(`Grand Total: $${result.total.toFixed(2)}`, 20, afterStitchLine + 40);
    doc.text(`Per Item Cost: $${result.perItemCost}`, 20, afterStitchLine + 50);

    doc.save("Embroidery_Estimate.pdf");
  };

  return (
    <div className="container" style={{
      maxWidth: 600,
      margin: "0 auto",
      paddingTop: 30,
      paddingBottom: 50,
      backgroundColor: "#f9f9f9",
      borderRadius: 8,
      boxShadow: "0 4px 8px rgba(0,0,0,0.1)"
    }}>
      <h1 className="text-3xl font-bold mb-4 text-center">Embroidery Price Calculator</h1>

      <div className="form-group mb-3">
        <label>Blank Garment Price ($):</label>
        <input type="number" step="0.01" className="form-control" value={blankPrice} onChange={(e) => setBlankPrice(e.target.value)} />
      </div>

      <div className="form-group mb-3">
        <label>Total Quantity:</label>
        <input type="number" step="0.01" className="form-control" value={totalQty} onChange={(e) => setTotalQty(e.target.value)} />
      </div>

      <div className="form-group mb-3">
        <label>Number of Locations:</label>
        <input type="number" className="form-control" value={numLocations} onChange={handleLocationChange} />
      </div>

      <div className="form-group mb-3">
        <label>Setup Fee ($):</label>
        <input type="number" step="0.01" className="form-control" value={setupFee} onChange={(e) => setSetupFee(e.target.value)} />
      </div>

      {stitchCounts.map((stitch, idx) => (
        <div className="form-group mb-3" key={idx}>
          <label>Stitch Count for Location {idx + 1}:</label>
          <input type="number" step="0.01" className="form-control" value={stitch} onChange={(e) => handleStitchChange(idx, e.target.value)} />
        </div>
      ))}

      <div className="form-group mb-3">
        <label>Profit Margin:</label>
        <select className="form-control" value={profitMargin} onChange={(e) => setProfitMargin(parseFloat(e.target.value))}>
          <option value={0.25}>25%</option>
          <option value={0.35}>35%</option>
          <option value={0.45}>45%</option>
          <option value={0.5}>50%</option>
          <option value={-1}>Custom</option>
        </select>
      </div>

      {profitMargin === -1 && (
        <div className="form-group mb-3">
          <label>Custom Margin (%):</label>
          <input type="number" step="0.01" className="form-control" value={customMargin} onChange={(e) => setCustomMargin(e.target.value)} />
        </div>
      )}

      <div className="text-center mb-4">
        <button className="btn btn-primary btn-lg" onClick={handleCalculate}>
          Calculate Price
        </button>
      </div>

      {result && (
        <div className="alert alert-success text-center">
          <h4>Grand Total: ${result.total.toFixed(2)}</h4>
          <h5>Per Item Cost: ${result.perItemCost}</h5>
        </div>
      )}

      {result && (
        <div className="text-center">
          <button className="btn btn-success" onClick={handleDownloadPDF}>
            Download PDF Quote
          </button>
        </div>
      )}
    </div>
  );
};

export default EmbCalci;
